import { forwardRef, FunctionComponent, SVGProps, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Modal, ModalBody } from "shared/UI/Modal/Modal";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { FeelingResources, GetUserTermsStatusResponseModel, useAcceptUserTermsFunctionRequest, useLogReportingEventFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { ReportingEventType } from "shared/constants/ReportingEventType";
import { useSupportMomentsReassuranceContext } from "core/state/SupportMomentsReassuranceContext/SupportMomentsReassuranceContext";
import { TermKeys } from "core/enums/enums";
import { trackEvents } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { SUPPORT_OPTION_CONFIG, SELECTION_DELAY_MS, COACHMARK_DELAY_MS, SupportOption } from "../../enums/supportMoments/supportMoments";
import { FloatingPopover } from "UIPalette/FloatingPopover/FloatingPopover";
import { SupportOptionsCoachmark } from "../Coachmarks/SupportOptionsCoachMark/SupportOptionsCoachmark";

interface SupportOptionCardProps {
  Emoji: FunctionComponent<SVGProps<SVGSVGElement>>;
  label: string;
  selected: boolean;
  onClick: () => void;
}

const SupportOptionCard = forwardRef<HTMLButtonElement, SupportOptionCardProps>(({ Emoji, label, selected, onClick }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className={classNames("shadow-md rounded-lg px-2 py-4 text-center transition-colors space-y-1", {
      "border-brand-purple bg-brand-purple text-white": selected,
      "border-brand-grey-light bg-white text-navy-blue": !selected,
    })}
  >
    <Emoji aria-hidden className="w-6 h-6 mx-auto" />
    <div className="font-semibold">{label}</div>
  </button>
));

interface SupportOptionsModalProps {
  onClose: () => void;
  resources?: FeelingResources[];
  coachmarkStatus?: GetUserTermsStatusResponseModel;
  isLoadingCoachmarkStatus: boolean;
  mutateCoachmarkStatus: () => void;
}

export const SupportOptionsModal = ({
  onClose,
  resources,
  coachmarkStatus,
  isLoadingCoachmarkStatus,
  mutateCoachmarkStatus,
}: SupportOptionsModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "supportMoments.supportOptionsModal" });
  const navigate = useNavigate();
  const { showReassurance } = useSupportMomentsReassuranceContext();
  const xClientCode = ClientInfoState.getCode();
  const [selectedOption, setSelectedOption] = useState<SupportOption | null>(null);
  const [showCoachmark, setShowCoachmark] = useState(false);
  const largerDisplay = useMatchMedia(ResponsiveBreakpoints.Tablet);

  const { trigger: triggerAcceptCoachmark } = useAcceptUserTermsFunctionRequest({ xClientCode });
  const { trigger: triggerLogReportingEvent } = useLogReportingEventFunctionRequest();

  useEffect(() => {
    if (isLoadingCoachmarkStatus || coachmarkStatus?.accepted) {
      return;
    }

    const timeout = setTimeout(() => {
      trackEvents(EventNames.SUPPORT_MOMENTS_COACHMARK_VIEWED, { step: "1" });
      setShowCoachmark(true);
    }, COACHMARK_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [isLoadingCoachmarkStatus, coachmarkStatus?.accepted]);

  const handleCoachmarkSkip = async (dismiss: () => void) => {
    trackEvents(EventNames.SUPPORT_MOMENTS_COACHMARK_SKIP, { step: "1" });
    setShowCoachmark(false);
    dismiss();

    await triggerAcceptCoachmark({
      query: {
        key: TermKeys.SupportMomentsTutorial,
      },
    });
    mutateCoachmarkStatus();
  };

  const handleCoachmarkNext = (dismiss: () => void) => {
    trackEvents(EventNames.SUPPORT_MOMENTS_COACHMARK_NEXT, { step: "1" });
    setShowCoachmark(false);
    dismiss();
  };

  const handleSelect = (option: SupportOption) => {
    if (selectedOption) {
      return;
    }

    triggerLogReportingEvent({
      body: {
        reportingEventType: ReportingEventType.SupportMomentBranchSelected,
        additionalInfo: option,
      },
    });

    if (option === SupportOption.JustChat) {
      triggerLogReportingEvent({
        body: {
          reportingEventType: ReportingEventType.SupportMomentCompleted,
          additionalInfo: option,
        },
      });
    }

    trackEvents(EventNames.SUPPORT_MOMENTS_OPTION_SELECTED, { value: option });
    setSelectedOption(option);
    setTimeout(() => {
      showReassurance(option, resources);
      onClose();
    }, SELECTION_DELAY_MS);
  };

  return (
    <Modal width={largerDisplay ? "xs" : "sm"} close={onClose} showDismissWithoutTitle>
      <ModalBody className="text-center mt-4">
        <h2>{t("heading")}</h2>
        <p>{t("subheading")}</p>
        <div className="grid grid-cols-2 gap-4 mt-6">
          {Object.values(SupportOption).map((option, index) => (
            <FloatingPopover
              key={index}
              floatingOffset={{
                mainAxis: 32,
                crossAxis: 80,
              }}
              initialOpenState={showCoachmark && option === SupportOption.Overwhelmed}
              trigger={(props) => (
                <SupportOptionCard
                  key={option}
                  ref={props.ref}
                  Emoji={SUPPORT_OPTION_CONFIG[option].Emoji}
                  label={t(`options.${option}`)}
                  selected={selectedOption === option}
                  onClick={() => handleSelect(option)}
                />
              )}
            >
              {({ dismiss }) => (
                <SupportOptionsCoachmark
                  step={1}
                  totalSteps={3}
                  onSkip={() => handleCoachmarkSkip(dismiss)}
                  onNext={() => handleCoachmarkNext(dismiss)}
                />
              )}
            </FloatingPopover>
          ))}
        </div>
        <button type="button" className="block mx-auto mt-6 mb-8 underline text-navy-blue" onClick={() => navigate("/positive-you-journal")}>
          {t("feelingGoodLink")}
        </button>
      </ModalBody>
    </Modal>
  );
};
